import React, { useEffect, useMemo, useState } from 'react';
import { MdSearch, MdAdd, MdEdit, MdDelete, MdVisibility, MdPeople } from 'react-icons/md';
import PageHeader from '../components/PageHeader';
import SkeletonRow from '../components/SkeletonRow';
import StatusToggle from '../../../Components/StatusToggle';
import { gatemanApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './BookRoom.css';

function Gatemen() {
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create | edit | view
  const [selected, setSelected] = useState(null);
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ query: '' });
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [gatemanToDelete, setGatemanToDelete] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateData, setDeactivateData] = useState({ id: null, name: '', reason: '' });

  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    password: '',
    status: 'ACTIVE'
  });

  const openCreate = () => {
    setModalMode('create');
    setFormData({ name: '', phoneNumber: '', email: '', password: '', status: 'ACTIVE' });
    setSelected(null);
    setError('');
    setSuccessMessage('');
    setShowModal(true);
  };

  const openEdit = (row) => {
    setModalMode('edit');
    setSelected(row);
    setFormData({
      name: row.name || '',
      phoneNumber: row.phoneNumber || '',
      email: row.email || '',
      password: '', // Don't populate password for edit
      status: row.status || 'ACTIVE'
    });
    setError('');
    setSuccessMessage('');
    setShowModal(true);
  };

  const openView = (row) => {
    setModalMode('view');
    setSelected(row);
    setFormData({
      name: row.name || '',
      phoneNumber: row.phoneNumber || '',
      email: row.email || '',
      password: '', // Don't show password in view mode
      status: row.status || 'ACTIVE'
    });
    setError('');
    setSuccessMessage('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  useEffect(() => {
    void loadList();
  }, []);

  const loadList = async () => {
    try {
      setIsTableLoading(true);
      setError('');
      const data = await gatemanApi.getAll();
      const arr = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : Array.isArray(data?.content) ? data.content : [];
      setList(arr);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to fetch gatemen');
    } finally {
      setIsTableLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = (filters.query || '').toLowerCase().trim();
    return list.filter((g) => {
      const name = (g.name || '').toLowerCase();
      const email = (g.email || '').toLowerCase();
      const phone = (g.phoneNumber || '').toLowerCase();
      return !q || name.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [list, filters]);

  const handleStatusToggle = async (row, nextIsActive) => {
    // If deactivating, open confirmation modal requiring reason
    if (!nextIsActive) {
      setDeactivateData({ id: row.id, name: row.name || '', reason: '' });
      setShowDeactivateModal(true);
      return;
    }
    // Activating can proceed directly
    try {
      const newStatus = nextIsActive ? 'ACTIVE' : 'INACTIVE';
      await gatemanApi.updateStatus(row.id, newStatus);
      await loadList();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to update status');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');
    try {
      if (modalMode === 'create') {
        await gatemanApi.add({
          name: formData.name,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          password: formData.password,
        });
        setSuccessMessage('Gateman added successfully');
        
        // Show success toast
        toast.success('Gateman added successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else if (modalMode === 'edit' && selected?.id) {
        const updateData = {
          name: formData.name,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
        };
        // Only include password if it's provided
        if (formData.password.trim()) {
          updateData.password = formData.password;
        }
        await gatemanApi.update(selected.id, updateData);
        setSuccessMessage('Gateman updated successfully');
        
        // Show success toast
        toast.success('Gateman updated successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      await loadList();
      setTimeout(() => { setShowModal(false); }, 600);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save';
      setError(errorMsg);
      
      // Show error toast
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    setGatemanToDelete(row);
  };

  const confirmDeleteGateman = async () => {
    if (!gatemanToDelete) return;
    try {
      await gatemanApi.delete(gatemanToDelete.id);
      await loadList();
      toast.success('Gateman deleted successfully');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to delete');
      toast.error(e.response?.data?.message || e.message || 'Failed to delete');
    } finally {
      setGatemanToDelete(null);
    }
  };

  const cancelDeleteGateman = () => setGatemanToDelete(null);

  return (
    <div className="admin-view gatemen-view">
      <PageHeader breadcrumbs={["Community", "Gatemen"]} />

      {/* Compact Header with Filters */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdPeople className="title-icon" />
            Gatemen Management
          </h2>
        </div>
        <div className="header-filters-compact">
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder="Search by name, email, or phone..."
              value={filters.query}
              onChange={(e) => setFilters({ ...filters, query: e.target.value })}
            />
          </div>
          <button className="btn-book-compact" onClick={openCreate}>
            <MdAdd />
            <span>Add Gateman</span>
          </button>
        </div>
      </div>

      <div className="table card">
        <div className="table-head">
          <div className="cell">Name</div>
          <div className="cell">Phone</div>
          <div className="cell">Email</div>
          <div className="cell">Status</div>
          <div className="cell text-right">Actions</div>
        </div>
        {isTableLoading && Array.from({ length: 6 }).map((_, idx) => (
          <SkeletonRow key={idx} />
        ))}

        {!isTableLoading && filtered && filtered.length > 0 && filtered.map((g, idx) => (
          <div className="table-row" key={g.id || idx}>
            <div className="cell">{g.name || ''}</div>
            <div className="cell">{g.phoneNumber || ''}</div>
            <div className="cell">{g.email || ''}</div>
            <div className="cell">
              <StatusToggle
                id={`gateman-status-${g.id}`}
                isActive={(g.status || 'ACTIVE') === 'ACTIVE'}
                onToggle={(next) => handleStatusToggle(g, next)}
              />
            </div>
            <div className="cell actions-cell">
              <button className="btn-action-compact btn-view" onClick={() => openView(g)} title="View">
                <MdVisibility />
              </button>
              <button className="btn-action-compact btn-edit" onClick={() => openEdit(g)} title="Edit">
                <MdEdit />
              </button>
              <button className="btn-action-compact btn-delete" onClick={() => handleDelete(g)} title="Delete">
                <MdDelete />
              </button>
            </div>
          </div>
        ))}

        {!isTableLoading && (!filtered || filtered.length === 0) && (
          <div className="table-row">
            <div className="cell no-results-cell">
              No gatemen found.
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{modalMode === 'create' ? 'Add Gateman' : modalMode === 'edit' ? 'Edit Gateman' : 'View Gateman'}</h3>
              <button 
                className="modal-close"
                onClick={closeModal}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={modalMode === 'view'}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    required
                    disabled={modalMode === 'view'}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={modalMode === 'view'}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={modalMode === 'create'}
                    disabled={modalMode === 'view'}
                    placeholder={modalMode === 'edit' ? 'Leave blank to keep current password' : ''}
                  />
                </div>
              </div>


              {error && (
                <div className="error-message inline-error">
                  {error}
                </div>
              )}
              {successMessage && (
                <div className="success-message inline-success">
                  {successMessage}
                </div>
              )}

              {modalMode !== 'view' && (
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="admin-btn admin-btn-secondary"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="admin-btn admin-btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Submit'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
      {showDeactivateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Deactivate Gateman</h3>
              <button 
                className="modal-close"
                onClick={() => { setShowDeactivateModal(false); setDeactivateData({ id: null, name: '', reason: '' }); }}
              >
                ×
              </button>
            </div>
            <div className="deactivate-form">
              <div className="warning-message">
                <strong>Warning:</strong> You are about to deactivate <strong>{deactivateData.name || 'this gateman'}</strong>.
                This action will prevent access to the system.
              </div>
              <div className="form-group">
                <label htmlFor="deactivateGatemanReason">
                  Reason for deactivation <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  id="deactivateGatemanReason"
                  name="reason"
                  value={deactivateData.reason}
                  onChange={(e) => setDeactivateData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Please provide a reason for deactivating this gateman..."
                  rows="4"
                  className="deactivate-reason-textarea"
                  required
                />
              </div>
              {error && (
                <div className="error-message">{error}</div>
              )}
              <div className="form-actions">
                <button 
                  type="button" 
                  className="admin-btn admin-btn-secondary"
                  onClick={() => { setShowDeactivateModal(false); setDeactivateData({ id: null, name: '', reason: '' }); }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="admin-btn admin-btn-danger"
                  onClick={async () => {
                    if (!deactivateData.reason.trim()) {
                      toast.warn('Please provide a reason for deactivating the gateman.');
                      return;
                    }
                    try {
                      await gatemanApi.updateStatus(deactivateData.id, 'INACTIVE');
                      setShowDeactivateModal(false);
                      setDeactivateData({ id: null, name: '', reason: '' });
                      await loadList();
                    } catch (e) {
                      setError(e.response?.data?.message || e.message || 'Failed to update status');
                    }
                  }}
                >
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {gatemanToDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button
                className="modal-close"
                onClick={cancelDeleteGateman}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                {`Are you sure you want to delete ${gatemanToDelete.name || 'this gateman'}?`}
              </p>
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelDeleteGateman}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDeleteGateman}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gatemen;



